import {
    regionalSiteConfig,
    TheWestRegionalSections,
    TheWestSection,
} from '@news-mono/common'
import {
    getCoralStoryId,
    getInlineEventAds,
    inlineArticleAds,
    metrics,
    RegionalReaderSurvey,
} from '@news-mono/component-library'
import {
    AdTargeting,
    BaseClientConfig,
    ContentDataTypes,
    GetRouteAdTargeting,
    PageType,
    Product,
    publicationAdKind,
    publicationAdPageType,
    RenderTarget,
    TogglesReduxState,
} from '@news-mono/web-common'
import {
    ArticleLikeV4DTO,
    isWestRegionalSection,
    PageMetaDTO,
} from '@west-australian-newspapers/publication-types'
import { layout } from '../../../App.routing'
import { getAmpArticleComposition } from './article.amp'
import { getNoIndexMeta } from './allowList'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'

// Used for Quicklinks
export const publicationPageType = 'publication'

export const createArticleRoute = ({
    article,
    config,
    section,
    getAdTargeting,
    pageMeta,
    renderTarget,
    toggles,
}: {
    article: ArticleLikeV4DTO
    config: BaseClientConfig
    section: TheWestSection
    getAdTargeting: GetRouteAdTargeting
    renderTarget: RenderTarget
    pageMeta?: PageMetaDTO
    toggles?: TogglesReduxState
}): PageType<TheWestSection> => {
    const featureState = toggles && toFeatureState(toggles)

    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        publicationAdKind[article.kind],
        section,
        article.topics.primary,
        ...article.topics.secondary,
    )

    const adTargeting: AdTargeting = {
        pageId: article.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    // Used to keep track of the promo card options setup on the west desk
    const westPromotionCardTopics = [
        'news',
        'sport',
        'business',
        'politics',
        'lifestyle',
        'entertainment',
        'travel',
        'technology',
        'features',
        'opinion',
    ]

    const primaryTopicId = article.topics.primary.parent
        ? article.topics.primary.parent.id
        : article.topics.primary.id

    const promoCardMetadataKey = `${
        westPromotionCardTopics.includes(primaryTopicId)
            ? primaryTopicId
            : 'news'
    }-promotional-card`

    const enableExtraInlineArticleAds = Boolean(
        featureState &&
            isFeatureEnabled(featureState, 'extra-inline-article-ads', false),
    )

    const enableAflMatchCentreDriver = Boolean(
        featureState &&
            isFeatureEnabled(featureState, 'afl-match-centre-driver', false),
    )

    const extraInlineArticleAds = [
        inlineArticleAds({
            insertAfter: 3,
            noticePosition: 'below-center',
            hiddenUntilLoaded: false,
            mastHead: 'thewest',
            slotID: 'article-mrec-one',
            size: enableExtraInlineArticleAds
                ? 'alwaysMrecMultiSize'
                : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
        inlineArticleAds({
            insertAfter: 12,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thewest',
            slotID: 'article-mrec-two',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
        inlineArticleAds({
            insertAfter: 21,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thewest',
            slotID: 'article-mrec-three',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
        inlineArticleAds({
            insertAfter: 30,
            noticePosition: 'below-center',
            hiddenUntilLoaded: true,
            mastHead: 'thewest',
            slotID: 'article-mrec-four',
            size: enableExtraInlineArticleAds ? 'alwaysMrec' : 'below768Mrec',
            pageType: publicationAdPageType[article.kind],
        }),
    ]

    return {
        kind: 'page',
        heading: article.heading,
        pageMeta: {
            ...pageMeta,
            description: article.homepageTeaser,
            title: article.seoHead || undefined,
            meta: getNoIndexMeta(article.source, section, featureState),
        },
        socialMeta: {
            title: article.socialHead || article.homepageHead,
            description: article.socialTeaser || article.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: publicationPageType,
        adTargeting,
        additionalPageProperties: {
            primaryTopic: article.topics.primary,
            secondaryTopics: article.topics.secondary,
            publicationKind: article.kind,
            // DPT-1375 extra Metadata for variant testing. Remove when finished
            requiredAccess: article.requiredAccess,
            layout: article.layoutView,
        },
        section,
        compositions:
            renderTarget === 'amp'
                ? [getAmpArticleComposition(article, section, config)]
                : [
                      layout.composition({
                          type: 'box',
                          props: {
                              containerWidth:
                                  metrics.thewest.siteMetrics.mainContentWidth,
                              verticalGutters: ['outerMargin', 'unset'],
                          },
                          contentAreas: {
                              main: [
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'outstream',
                                      props: {
                                          noticePosition: 'below-center',
                                          hiddenUntilLoaded: false,
                                          padding: [0],
                                          slot: {
                                              id: 'outstream2x2',
                                              size: 'outstream',
                                          },
                                          adType: 'inline',
                                      },
                                  }),
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'ad-tech-dfp-testing',
                                      props: {
                                          noticePosition: 'below-center',
                                          hiddenUntilLoaded: false,
                                          padding: [0],
                                          slot: {
                                              id: '2x1_test_pixel',
                                              size: '2x1_test_pixel',
                                          },
                                          adType: 'inline',
                                      },
                                  }),
                                  ...getBreakingNewsStrap(section),
                              ],
                          },
                      }),
                      layout.composition({
                          type: 'athena',
                          props: {
                              containerWidth:
                                  metrics.thewest.siteMetrics.mainContentWidth,
                              sidebarOptions: 'hiddenMobile',
                              elementType: 'article',
                          },
                          contentAreas: {
                              sidebar: [...getSidebar(article, section)],

                              main: [
                                  layout.component({
                                      type: 'topic-list',
                                      props: {
                                          topics: article.topics,
                                          verticalSpacing: 'sm',
                                      },
                                  }),

                                  layout.component({
                                      type: 'article',
                                      props: {
                                          meta: {
                                              slug: article.slug,
                                              kind: 'article',
                                              section: section,
                                              inlinePublicationContent:
                                                  section === 'genwest'
                                                      ? []
                                                      : [
                                                            {
                                                                kind: 'the-west-live-in-article-banner',
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'inline-queen-tribute-banner',
                                                                props: {
                                                                    heading:
                                                                        'Share your personal tribute to \n Queen Elizabeth II',
                                                                    subText:
                                                                        'Honour Her Majesty ahead of her final farewell.',
                                                                    buttonText:
                                                                        'Find out more',
                                                                    color: '#640C7C',
                                                                    driverLink:
                                                                        '/news/queen-elizabeth-ii/tributes',
                                                                    feature:
                                                                        'queens-tribute-in-article-banners',
                                                                },
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'inline-go-matildas-banner',
                                                                props: {
                                                                    heading:
                                                                        'Share your support for our Matildas in the World\xa0Cup',
                                                                    subText:
                                                                        'Post your message for the team here.',
                                                                    buttonText:
                                                                        'Find out more',
                                                                    color: '#006331',
                                                                    driverLink:
                                                                        '/matildasmessage',
                                                                    feature:
                                                                        'go-matildas-article-banner',
                                                                },
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'inline-court-in-the-act-banner',
                                                                props: {
                                                                    heading:
                                                                        'LISTEN to the new podcast \n Court in the Act',
                                                                    subText:
                                                                        'Inside the courtroom with Tim Clarke.',
                                                                    buttonText:
                                                                        'Find out more',
                                                                    color: '#1B2666',
                                                                    buttonHoverColor:
                                                                        '#FFFFFF',
                                                                    driverLink:
                                                                        'https://thewest.com.au/podcasts/court-in-the-act',
                                                                    feature:
                                                                        'court-in-the-act-article-banner',
                                                                },
                                                                insertAfter: 8,
                                                            },
                                                            {
                                                                kind: 'inline-vanishing-cousins-banner',
                                                                props: {
                                                                    heading:
                                                                        'Watch The West’s New WA true crime series: Vanishing Cousins',
                                                                    subText: '',
                                                                    buttonText:
                                                                        'Watch Now',
                                                                    color: '#1B2666',
                                                                    buttonHoverColor:
                                                                        '#FFFFFF',
                                                                    driverLink:
                                                                        'https://thewest.com.au/vanishing-ep1',
                                                                    feature:
                                                                        'vanishing-cousins-article-banner',
                                                                },
                                                                insertAfter: 8,
                                                            },
                                                            {
                                                                kind: 'inline-readers-survey-banner',
                                                                props: {
                                                                    displayCondition:
                                                                        true,
                                                                    visiblility:
                                                                        'visible',
                                                                    ...RegionalReaderSurvey,
                                                                    feature:
                                                                        'regional-readers-survey',
                                                                },
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'sport-call-to-action',
                                                                insertAfter: 6,
                                                            },
                                                            {
                                                                kind: 'sports-hub-call-to-action',
                                                                insertAfter: 3,
                                                            },
                                                            {
                                                                kind: 'inline-match-centre-banner',
                                                                insertAfter: 10,
                                                                props: {
                                                                    url: '/sport/afl/fixtures',
                                                                    toggle: enableAflMatchCentreDriver,
                                                                },
                                                            },
                                                            {
                                                                kind: 'oovvuu-featured-card',
                                                                insertAfter: 7,
                                                                props: {
                                                                    oovvuuUrl:
                                                                        config.oovvuuUrl,
                                                                    oovvuuPath:
                                                                        config.oovvuuPath,
                                                                },
                                                            },
                                                            ...extraInlineArticleAds,
                                                            ...(article.kind ===
                                                            'event'
                                                                ? getInlineEventAds(
                                                                      Product.TheWest,
                                                                  )
                                                                : []),
                                                        ],
                                          },
                                          dataDefinitionArgs: {
                                              publicationId: article.id,
                                          },
                                          section,
                                          isVideoStickyEnabled: true,
                                      },
                                  }),

                                  layout.component({
                                      type: 'content-separator',
                                      props: {
                                          verticalBottomSpacing: 'lg',
                                      },
                                  }),

                                  layout.nestedComposition({
                                      type: 'box',
                                      props: {
                                          verticalSpacing: 'lg',
                                      },
                                      contentAreas: {
                                          main: [
                                              layout.component({
                                                  type: 'coral',
                                                  feature: 'comments',
                                                  props: {
                                                      storyID: getCoralStoryId(
                                                          section,
                                                          config.publicHostname,
                                                          article.id,
                                                      ),
                                                      articleCommentsEnabled:
                                                          article.allowCommenting,
                                                  },
                                              }),
                                          ],
                                      },
                                  }),

                                  layout.nestedComposition({
                                      type: 'janus',
                                      props: {
                                          verticalSpacing: 'sm',
                                          stretchColumns: true,
                                          layoutRatio: '2:3',
                                          breakpoints: {
                                              layoutRatiosBreakpoint: 'xs',
                                          },
                                      },
                                      contentAreas: {
                                          left: [
                                              layout.component({
                                                  type: 'curated-promotional-card',
                                                  props: {
                                                      fontScale: 1,
                                                      verticalSpacing: 'lg',
                                                      fixedRatios: {
                                                          default: ['4:3'],
                                                          md: '16:9',
                                                      },
                                                      hasBackground: true,
                                                      hasPadding: true,
                                                      mediaMode: 'edgeToEdge',
                                                      showCTA: true,

                                                      dataDefinitionArgs: {
                                                          type: 'active-promotion',
                                                          promotionSlotId:
                                                              primaryTopicId,
                                                          metadataKey:
                                                              promoCardMetadataKey,
                                                      },
                                                  },
                                              }),
                                          ],
                                          right: [
                                              ...relatedContent(
                                                  article,
                                                  section,
                                              ),
                                          ],
                                      },
                                  }),
                                  layout.nestedComposition({
                                      type: 'janus',
                                      feature: 'ripple',
                                      props: {
                                          stretchColumns: true,
                                          layoutRatio: '1:1',
                                      },
                                      contentAreas: {
                                          left: [
                                              layout.component({
                                                  type: 'ripple',
                                                  props: {
                                                      pageSection:
                                                          getRippleSectionTargeting(
                                                              section,
                                                              article,
                                                          ),
                                                  },
                                              }),
                                          ],

                                          right: [
                                              layout.component({
                                                  type: 'ripple',
                                                  props: {
                                                      pageSection:
                                                          getRippleSectionTargeting(
                                                              section,
                                                              article,
                                                          ),
                                                  },
                                              }),
                                          ],
                                      },
                                  }),
                                  ...articleFollowers(section),
                                  ...taboola(article, section),
                              ],
                          },
                      }),
                  ],
    }
}

const isGenWestArticle = (article: ArticleLikeV4DTO) => {
    if (
        article.topics.primary.id === 'genwest' ||
        article.topics.primary.parent?.id === 'genwest' ||
        (article.topics.secondary &&
            article.topics.secondary.findIndex(
                (topic) => topic.parent?.id === 'genwest',
            ) !== -1)
    ) {
        return true
    }

    return false
}

const taboola = (article: ArticleLikeV4DTO, section: TheWestSection) => {
    return [
        isGenWestArticle(article)
            ? layout.component({
                  type: 'taboola',
                  feature: 'genwest-taboola',
                  props: {
                      taboolaContainerId: 'taboola-below-article-thumbnails',
                      locationType: 'article',
                  },
              })
            : layout.component({
                  type: 'taboola',
                  feature:
                      section !== 'default' ? 'taboola-regionals' : 'taboola',
                  props: {
                      taboolaContainerId: 'taboola-below-article-thumbnails',
                      locationType: 'article',
                  },
              }),
    ]
}

const relatedContent = (article: ArticleLikeV4DTO, section: TheWestSection) => {
    return [
        isGenWestArticle(article)
            ? layout.component({
                  type: 'related-content',
                  props: {
                      dataDefinitionArgs: {
                          type: 'curation',
                          name: 'genwest',
                          offset: 0,
                          pageSize: 6,
                      },
                  },
              })
            : layout.component({
                  type: 'publication-related-content',
                  props: {
                      dataDefinitionArgs: {
                          publicationId: article.id,
                      },
                  },
              }),
    ]
}

const getSidebarHeading = (section: TheWestSection) => {
    if (section === 'default' || section === 'genwest') {
        // the west
        return 'Top Stories'
    }

    // A regional masthead
    return 'Local News'
}

const getSidebar = (article: ArticleLikeV4DTO, section: TheWestSection) => {
    if (section === 'genwest') {
        return [
            layout.component({
                type: 'ad-unit',
                feature: 'genwest-ad-slots',
                props: {
                    noticePosition: 'below-center',
                    padding: [0, 0, metrics.thewest.margins.md, 0],
                    slot: {
                        id: 'sidebar-top',
                        size: 'desktopMrecHalfPage',
                        pageType: publicationAdPageType[article.kind],
                    },
                    adType: 'inline',
                },
            }),
            layout.nestedComposition({
                type: 'box',
                props: {
                    stickyOffset: 350,
                    fillContainer: true,
                },
                contentAreas: {
                    main: [
                        layout.component({
                            type: 'ad-unit',
                            feature: 'genwest-ad-slots',
                            props: {
                                noticePosition: 'below-center',
                                padding: [0, 0, metrics.thewest.margins.md, 0],
                                slot: {
                                    id: 'sidebar-middle',
                                    size: 'desktopMrec',
                                    pageType:
                                        publicationAdPageType[article.kind],
                                },
                                adType: 'inline',
                            },
                        }),

                        layout.component({
                            type: 'juliet',
                            props: {
                                cardType: {
                                    type: 'landscape',
                                    format: 'landscape-smallImage',
                                },
                                removeHorizontalGutters: true,
                                kickerMode: 'hidden',
                                contentIsTangential: true,
                                sectionHeader: {
                                    heading: getSidebarHeading(section),
                                    section: 'generic',
                                },
                                dataDefinitionArgs: getSidebarDataDefinition(
                                    section,
                                    article,
                                ),
                            },
                        }),
                    ],
                },
            }),
        ]
    }

    return [
        layout.component({
            type: 'ad-unit',
            props: {
                noticePosition: 'below-center',
                padding: [0, 0, metrics.thewest.margins.md, 0],
                slot: {
                    id: 'sidebar-top',
                    size: 'desktopMrecHalfPage',
                    pageType: publicationAdPageType[article.kind],
                },
                adType: 'inline',
            },
        }),
        layout.nestedComposition({
            type: 'box',
            props: {
                stickyOffset: 350,
                fillContainer: true,
            },
            contentAreas: {
                main: [
                    layout.component({
                        type: 'ad-unit',
                        props: {
                            noticePosition: 'below-center',
                            padding: [0, 0, metrics.thewest.margins.md, 0],
                            slot: {
                                id: 'sidebar-middle',
                                size: 'desktopMrec',
                                pageType: publicationAdPageType[article.kind],
                            },
                            adType: 'inline',
                        },
                    }),

                    layout.component({
                        type: 'juliet',
                        props: {
                            cardType: {
                                type: 'landscape',
                                format: 'landscape-smallImage',
                            },
                            removeHorizontalGutters: true,
                            kickerMode: 'hidden',
                            contentIsTangential: true,
                            sectionHeader: {
                                heading: getSidebarHeading(section),
                                section: 'generic',
                            },
                            dataDefinitionArgs: getSidebarDataDefinition(
                                section,
                                article,
                            ),
                        },
                    }),
                ],
            },
        }),
    ]
}

const articleFollowers = (section: TheWestSection) => {
    // Is this the west of a regional masthead?
    const isTheWest = section === 'default'

    const components = []

    if (isTheWest) {
        components.push(
            layout.component({
                feature: 'newsletter-subscription-banner',
                type: 'newsletter-signup',
                props: {
                    text: 'The latest from The West delivered to your inbox daily.',
                    buttonLabel: 'Sign Up',
                },
            }),
        )
    }

    return components
}

const getRippleSectionTargeting = (
    section: TheWestSection,
    article: ArticleLikeV4DTO,
) => {
    if (section !== 'default') {
        return section
    } else {
        return article.topics.primary.parent
            ? article.topics.primary.parent.id
            : article.topics.primary.id
    }
}

const getSidebarDataDefinition = (
    section: TheWestSection,
    article: ArticleLikeV4DTO,
): ContentDataTypes => {
    if (isWestRegionalSection(section)) {
        const regionalConfig =
            regionalSiteConfig[section as TheWestRegionalSections]
        if (regionalConfig) {
            return {
                type: 'listing',
                topics: [regionalConfig.mastheadTopic],
                paging: {
                    page: 1,
                    pageSize: 3,
                },
            }
        }
    }

    return {
        type: 'curation',
        name: isGenWestArticle(article) ? 'genwest' : 'home',
        offset: 0,
        pageSize: 3,
    }
}

const getBreakingNewsStrap = (section: TheWestSection) => {
    if (isWestRegionalSection(section) || section === 'genwest') {
        // For now at least we dont want the breaking news strap on any regional masthead or genwest pages
        return []
    }

    return [
        layout.component({
            type: 'breaking-news',
            props: {
                dataDefinitionArgs: {
                    type: 'curation',
                    name: 'breaking-news',
                    offset: 0,
                    pageSize: 1,
                },
            },
        }),
        layout.component({
            type: 'breaking-news-promo-strap',
            props: {
                dataDefinitionArgs: {
                    type: 'curation',
                    name: 'breaking-news-promo-strap',
                    offset: 0,
                    pageSize: 1,
                },
            },
        }),
    ]
}
